import * as React from 'react'
import { X } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type DrawerWidth = 'default' | 'medium' | 'mediumPlus' | 'wide' | 'extraWide' | 'nearFull' | 'full'

export interface DrawerProps {
  /** Whether the drawer is open */
  open: boolean
  /** Callback when drawer should close */
  onClose: () => void
  /** Title displayed in the drawer header */
  title: string
  /** Optional subtitle/description below title */
  subtitle?: string
  /**
   * A badge sitting on the title's own line, after it — what this record IS, when that is not
   * already in the words of the title (an activity's type, say).
   *
   * Separate from `headerContent`, which is the band BELOW the title: that band is for state
   * ("Overdue", "Called off") and actions, things that come and go. Identity belongs next to the
   * name. The title keeps `truncate` and the badge does not shrink, so a long title gives way
   * rather than squeezing the badge out.
   */
  titleBadge?: React.ReactNode
  /** Drawer width: 'default' (560px), 'medium' (640px), 'wide' (70%, min 900px, max 1200px),
   *  'extraWide' (80vw, capped 1400px), 'nearFull' (95vw, uncapped), 'full' (100%) */
  width?: DrawerWidth
  /** Main content of the drawer */
  children: React.ReactNode
  /** Optional footer content (actions, buttons) - fixed at bottom */
  footer?: React.ReactNode
  /** Optional content for the header area below title (e.g., AnchorNav) */
  headerContent?: React.ReactNode
  /** Additional className for the content area */
  className?: string
}

const widthClasses: Record<DrawerWidth, string> = {
  default: '!w-[560px] !max-w-[90vw] sm:!max-w-[560px]',
  medium: '!w-[640px] !max-w-[90vw] sm:!max-w-[640px]',
  mediumPlus: '!w-[760px] !max-w-[90vw] sm:!max-w-[760px]',
  wide: '!w-[70vw] !min-w-[1000px] !max-w-[1200px] sm:!max-w-[1200px]',
  extraWide: '!w-[80vw] !min-w-[1100px] !max-w-[1400px] sm:!max-w-[1400px]',
  /*
    NO CAP, unlike `extraWide`. A record whose drawer is a WORKSPACE — a form beside a rail
    beside a state column — runs out of room at 1400px on a wide monitor and starts wrapping,
    while `full` leaves no sliver of the page behind it and stops reading as a drawer at all.
    95vw is the geometry `TaskDetailModal` uses for the same reason.
  */
  nearFull: '!w-[95vw] !max-w-[95vw] sm:!max-w-[95vw]',
  full: '!w-screen sm:!max-w-none',
}

/**
 * Drawer component for detail views
 *
 * Features:
 * - Slides from right edge
 * - Fixed header with title and close button
 * - Scrollable content area
 * - Optional fixed footer
 * - Escape key and backdrop click close the drawer
 *
 * Usage:
 * ```tsx
 * <Drawer
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Quote Details"
 *   subtitle="QT-2026-00145"
 *   footer={<Button>Save</Button>}
 * >
 *   <div>Content</div>
 * </Drawer>
 * ```
 */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  titleBadge,
  width = 'default',
  children,
  footer,
  headerContent,
  className,
}: DrawerProps): JSX.Element {
  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent
        side="right"
        hideCloseButton
        className={cn(
          'flex flex-col p-0 gap-0',
          widthClasses[width],
          // Override default Sheet max-width
          'sm:max-w-none'
        )}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 border-b border-border">
          <SheetHeader className="p-4 pb-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <SheetTitle className="text-lg font-semibold truncate">
                    {title}
                  </SheetTitle>
                  {titleBadge}
                </div>
                {subtitle && (
                  <SheetDescription className="text-sm text-muted-foreground mt-0.5 truncate">
                    {subtitle}
                  </SheetDescription>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0 -mr-2 -mt-1"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </SheetHeader>
          {headerContent && (
            <div className="px-4 pb-3">
              {headerContent}
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className={cn('flex-1 overflow-y-auto', className)}>
          {children}
        </div>

        {/* Fixed Footer */}
        {footer && (
          <div className="flex-shrink-0 border-t border-border p-4 bg-background">
            {footer}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
